// iOS Settings-style detail view for one catalog item. Visual primitives,
// per-kind cards, and sheet mounts live under ./detail/.

import { generateTagName } from "@betterpos/domain";
import { useState } from "react";
import type {
  AmmoAttributes,
  FirearmAttributes,
  HolsterAttributes,
  ItemDetail,
  OpticsAttributes,
  Photo,
} from "../data/catalog.ts";
import { formatCents } from "../data/catalog.ts";
import { AttrRow } from "./detail/AttrRow.tsx";
import { AmmoSpecLine, buildAmmoRows } from "./detail/ammo.tsx";
import { FeaturesEditor } from "./detail/FeaturesEditor.tsx";
import { buildFirearmRows, FirearmSpecLine } from "./detail/firearm.tsx";
import { HeroPhoto } from "./detail/HeroPhoto.tsx";
import { buildHolsterRows, HolsterSpecLine } from "./detail/holster.tsx";
import { ItemDetailSheets } from "./detail/ItemDetailSheets.tsx";
import { GroupedCard, Row, Section } from "./detail/layout.tsx";
import { buildOpticsRows, OpticsSpecLine } from "./detail/optics.tsx";
import { AttrPill, ConditionPill, PricePill } from "./detail/pills.tsx";
import { buildTagNameInputs } from "./detail/tag-name-inputs.ts";
import { BackChevronIcon } from "./icons.tsx";
import { PhotoStrip } from "./PhotoStrip.tsx";
import { formatDate, primaryBarcode } from "./utils.ts";

export function ItemDetailView({ detail, onBack }: { detail: ItemDetail; onBack: () => void }) {
  const { brand, category, unit, taxCategory, barcodes, variants, prices } = detail;

  // Photos are the one thing on this page that mutate while it's open
  // (add / delete). Owning the state here keeps the strip's optimistic
  // updates in sync without forcing the parent to refetch the whole item.
  const [photos, setPhotos] = useState<Photo[]>(detail.photos ?? []);

  // Edit-driven mutable state. Each successful PATCH bubbles up the new
  // version + value so the next edit sends the right expected_version
  // without a full refetch.
  const [item, setItem] = useState(detail.item);
  const [firearmAttributes, setFirearmAttributes] = useState<FirearmAttributes | null>(
    detail.firearmAttributes,
  );
  // Per-kind attributes (PR A read-only, PR B editable). Each block has
  // its own useState so a successful PATCH bubbles the new version back
  // without a full refetch — same pattern as firearmAttributes above.
  const [ammoAttributes, setAmmoAttributes] = useState<AmmoAttributes | null>(
    detail.ammoAttributes,
  );
  const [opticsAttributes, setOpticsAttributes] = useState<OpticsAttributes | null>(
    detail.opticsAttributes,
  );
  const [holsterAttributes, setHolsterAttributes] = useState<HolsterAttributes | null>(
    detail.holsterAttributes,
  );
  const [tagSheetOpen, setTagSheetOpen] = useState(false);
  const [ammoSheetOpen, setAmmoSheetOpen] = useState(false);
  const [opticsSheetOpen, setOpticsSheetOpen] = useState(false);
  const [holsterSheetOpen, setHolsterSheetOpen] = useState(false);

  const isFirearm = Boolean(firearmAttributes);

  // Collect the input blocks for generateTagName() so firearm, ammo, optics
  // and holster all run through the same dispatcher. Only one block drives
  // the formula at a time — see resolveKindUsed() in packages/domain.
  const tagNameInputs = buildTagNameInputs({
    kind: item.kind,
    displayName: item.displayName,
    firearm: firearmAttributes,
    ammo: ammoAttributes,
    optics: opticsAttributes,
    holster: holsterAttributes,
  });

  // Formula-only tag name (what the title falls back to when there's no
  // override). Shown as placeholder + preview in the override sheet.
  const formulaTagName = generateTagName({ ...tagNameInputs, override: null });
  // Auto-generated tag name (what prints on labels and shows as the big
  // title). Honors item.tagNameOverride when set.
  const tagName = generateTagName({
    ...tagNameInputs,
    override: item.tagNameOverride ?? null,
  });
  const features = firearmAttributes?.features ?? [];
  const showLowerTitle = item.displayName.trim() !== tagName.value.trim();

  return (
    <div className="min-h-screen md:min-h-full">
      {/* Narrow-screen back bar — iOS translucent nav */}
      <div className="md:hidden sticky top-0 z-10 bg-slate-50/80 backdrop-blur-xl border-b border-slate-200/70 px-2 py-2.5 flex items-center">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-[17px] text-[#007aff] font-medium px-2 py-1 -ml-1 active:opacity-60"
          aria-label="Back to list"
        >
          <BackChevronIcon />
          <span>Products</span>
        </button>
      </div>

      <div className="px-5 md:px-8 pt-5 md:pt-10 pb-14 space-y-5">
        {/* Hero photo — only when a real photo exists. Empty state defers
            to the PhotoStrip below, which has the add-photo affordance. */}
        {photos[0]?.signedUrl && <HeroPhoto photo={photos[0]} alt={item.displayName} />}

        {/* Title block.
            Top (bold) = auto-generated tag name — what prints on labels.
            Lower (medium) = long-form display name — user-typed, no cap.
            Then: firearm spec one-liner → price/age pills → features chips → description. */}
        <header className="space-y-2">
          <button
            type="button"
            onClick={() => setTagSheetOpen(true)}
            className="block text-left w-full group"
            aria-label="Edit tag name"
          >
            <h2 className="text-[28px] md:text-[32px] font-bold tracking-tight leading-[1.1] text-slate-900 break-words group-hover:text-slate-700 group-active:text-slate-500 transition">
              {tagName.value}
            </h2>
          </button>
          {showLowerTitle && (
            <p className="text-[17px] text-slate-700 leading-snug">{item.displayName}</p>
          )}

          {firearmAttributes && <FirearmSpecLine a={firearmAttributes} />}
          {!firearmAttributes && ammoAttributes && <AmmoSpecLine a={ammoAttributes} />}
          {!firearmAttributes && opticsAttributes && <OpticsSpecLine a={opticsAttributes} />}
          {!firearmAttributes && holsterAttributes && <HolsterSpecLine a={holsterAttributes} />}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {firearmAttributes && <ConditionPill condition={firearmAttributes.conditionNewUsed} />}
            <PricePill cents={item.defaultPriceCents} />
            {item.minAge && <AttrPill label={`${item.minAge}+`} />}
          </div>

          {firearmAttributes && (
            <div className="pt-1">
              <FeaturesEditor
                itemId={item.id}
                expectedVersion={firearmAttributes.version}
                features={features}
                onSaved={(next) =>
                  setFirearmAttributes((prev) =>
                    prev ? { ...prev, features: next.features ?? [], version: next.version } : prev,
                  )
                }
              />
            </div>
          )}

          {item.description && (
            <p className="text-[17px] leading-snug text-slate-700 pt-1">{item.description}</p>
          )}

          {item.restrictedFlag && item.restrictedReason && (
            <div className="rounded-xl bg-ios-red/10 px-4 py-3 mt-2">
              <p className="text-[15px] font-semibold text-ios-red uppercase tracking-wide">
                Restricted
              </p>
              <p className="text-[17px] text-ios-red mt-1">{item.restrictedReason}</p>
            </div>
          )}
        </header>

        {/* Photo management — add/delete affordances */}
        <PhotoStrip itemId={item.id} photos={photos} onPhotosChange={setPhotos} />

        {/* Context */}
        <Section>
          <GroupedCard>
            <Row label="UPC" value={primaryBarcode(barcodes) ?? "—"} mono />
            <Row label="SKU" value={item.sku} mono />
            <Row label="Brand" value={brand?.displayName ?? "—"} />
            <Row label="Category" value={category?.displayName ?? "—"} />
            <Row label="Unit" value={unit?.displayName ?? "—"} />
            <Row label="Tax" value={taxCategory?.displayName ?? "—"} />
            <Row label="MSRP" value={item.msrpCents ? formatCents(item.msrpCents) : "—"} mono />
            <Row label="MAP" value={item.mapCents ? formatCents(item.mapCents) : "—"} mono />
            <Row
              label="Reorder"
              value={
                item.reorderMin != null || item.reorderMax != null
                  ? `${item.reorderMin ?? "—"} / ${item.reorderMax ?? "—"}`
                  : "—"
              }
              mono
            />
            <Row label="Can sell offline" value={item.offlineSellable ? "Yes" : "No"} last />
          </GroupedCard>
        </Section>

        {/* Firearm attributes — ATF bound-book fields first, then flow of
             reading. Required rows (per 27 CFR 478.125(e)) can't be blank
             under the current schema; the UI shows a red placeholder if
             one is anyway (defence-in-depth). Optional empty rows show a
             neutral "Tap to add" placeholder — real inline editing lands
             in Phase 3. */}
        {isFirearm && firearmAttributes && (
          <Section>
            <GroupedCard>
              {buildFirearmRows(firearmAttributes).map((r, i, arr) => (
                <AttrRow key={r.label} row={r} last={i === arr.length - 1} />
              ))}
            </GroupedCard>
          </Section>
        )}

        {/* Per-kind attribute cards (PR A). At most one per item — the
             DB can hold rows in multiple tables for an item but, by
             convention, only one kind of structured attribute attaches to
             an item. We render firearm first (bound-book-tied), then ammo
             / optics / holster in that order. */}
        {!isFirearm && ammoAttributes && (
          <Section>
            <button
              type="button"
              onClick={() => setAmmoSheetOpen(true)}
              className="block w-full text-left group"
              aria-label="Edit ammo attributes"
            >
              <GroupedCard>
                {buildAmmoRows(ammoAttributes).map((r, i, arr) => (
                  <AttrRow key={r.label} row={r} last={i === arr.length - 1} />
                ))}
              </GroupedCard>
            </button>
          </Section>
        )}

        {!isFirearm && opticsAttributes && (
          <Section>
            <button
              type="button"
              onClick={() => setOpticsSheetOpen(true)}
              className="block w-full text-left group"
              aria-label="Edit optics attributes"
            >
              <GroupedCard>
                {buildOpticsRows(opticsAttributes).map((r, i, arr) => (
                  <AttrRow key={r.label} row={r} last={i === arr.length - 1} />
                ))}
              </GroupedCard>
            </button>
          </Section>
        )}

        {!isFirearm && holsterAttributes && (
          <Section>
            <button
              type="button"
              onClick={() => setHolsterSheetOpen(true)}
              className="block w-full text-left group"
              aria-label="Edit holster attributes"
            >
              <GroupedCard>
                {buildHolsterRows(holsterAttributes).map((r, i, arr) => (
                  <AttrRow key={r.label} row={r} last={i === arr.length - 1} />
                ))}
              </GroupedCard>
            </button>
          </Section>
        )}

        {/* Barcodes */}
        {barcodes.length > 0 && (
          <Section heading="Barcodes">
            <GroupedCard>
              {barcodes.map((b, i) => (
                <Row
                  key={b.id}
                  label={b.isPrimary ? "Primary" : "Alternate"}
                  value={
                    <span>
                      <span className="font-mono tracking-tight">{b.code}</span>
                      {b.packQty > 1 && <span className="ml-2 text-slate-500">× {b.packQty}</span>}
                    </span>
                  }
                  last={i === barcodes.length - 1}
                />
              ))}
            </GroupedCard>
          </Section>
        )}

        {/* Variants */}
        {variants.length > 0 && (
          <Section heading="Variants">
            <GroupedCard>
              {variants.map((v, i) => (
                <div
                  key={v.id}
                  className={`px-4 py-3.5 ${i < variants.length - 1 ? "border-b border-slate-200/80" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[17px] text-slate-900 font-medium leading-tight">
                        {v.displayName}
                      </p>
                      <p className="text-[15px] text-slate-500 font-mono tracking-tight mt-0.5">
                        {v.variantSku}
                      </p>
                      {(v.dim1 || v.dim2 || v.dim3) && (
                        <p className="text-[15px] text-slate-500 mt-1">
                          {[v.dim1, v.dim2, v.dim3]
                            .filter((d): d is { name: string; value: string } => d !== null)
                            .map((d) => `${d.name}: ${d.value}`)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                    {v.priceOverrideCents && (
                      <span className="text-[17px] text-slate-700 tabular-nums shrink-0">
                        {formatCents(v.priceOverrideCents)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </GroupedCard>
          </Section>
        )}

        <ItemDetailSheets
          item={item}
          formulaPreview={formulaTagName.value}
          tagSheetOpen={tagSheetOpen}
          onTagCancel={() => setTagSheetOpen(false)}
          onTagSaved={(next) => {
            setItem((prev) => ({
              ...prev,
              tagNameOverride: next.tagNameOverride,
              version: next.version,
            }));
            setTagSheetOpen(false);
          }}
          ammoAttributes={ammoAttributes}
          ammoSheetOpen={ammoSheetOpen}
          onAmmoCancel={() => setAmmoSheetOpen(false)}
          onAmmoSaved={(next) => {
            setAmmoAttributes(next);
            setAmmoSheetOpen(false);
          }}
          opticsAttributes={opticsAttributes}
          opticsSheetOpen={opticsSheetOpen}
          onOpticsCancel={() => setOpticsSheetOpen(false)}
          onOpticsSaved={(next) => {
            setOpticsAttributes(next);
            setOpticsSheetOpen(false);
          }}
          holsterAttributes={holsterAttributes}
          holsterSheetOpen={holsterSheetOpen}
          onHolsterCancel={() => setHolsterSheetOpen(false)}
          onHolsterSaved={(next) => {
            setHolsterAttributes(next);
            setHolsterSheetOpen(false);
          }}
        />

        {/* Prices */}
        {prices.length > 0 && (
          <Section heading="Price history">
            <GroupedCard>
              {prices.map((p, i) => (
                <div
                  key={p.id}
                  className={`px-4 py-3.5 ${i < prices.length - 1 ? "border-b border-slate-200/80" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[17px] text-slate-500">{p.priceLevel.displayName}</p>
                      <p className="text-[15px] text-slate-500 mt-0.5">
                        {formatDate(p.effectiveFrom)}
                        <span className="mx-1 text-slate-300">→</span>
                        {formatDate(p.effectiveTo)}
                      </p>
                      {p.notes && <p className="text-[15px] text-slate-500 mt-1">{p.notes}</p>}
                    </div>
                    <span className="text-[22px] text-slate-900 font-semibold tabular-nums shrink-0">
                      {formatCents(p.priceCents)}
                    </span>
                  </div>
                </div>
              ))}
            </GroupedCard>
          </Section>
        )}
      </div>
    </div>
  );
}
